import { capitalize } from 'lodash';
import { FC, ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { singleProductActions, singleProductSelectors } from '../../../store/single-product';
import { productsActions, productsSelectors } from '../../../store/products';
import { RootState } from '../../../store/store';
import { Product } from '../../../types';
import {
  StyledModal,
  ModalContent,
  Title,
  Subtitle,
  CloseModalButton,
  CloseModalIcon,
  ModalFooter,
  Spinner,
  CloseButton,
  UpdateButton,
  ModalBody,
  BodySection,
  SectionColumn,
  SectionDivider,
  TextDataValue,
  InputStyle,
  RadioFormLabel,
  RadiobButtonsGroup,
  RadioButtonLabel,
  RadioButton,
  Alert,
  Snackbar,
  ErrorSnackbar,
  LabelStyle,
  ImageDataValue,
  SectionDividerMarginTop,
  FooterDataValue,
  SelectCategoryFormControl,
  SelectSubcategoryFormControl,
  SelectInputLabel,
  SelectMenuProps,
  StyledSelectField,
  StyledSelectMenuItem,
  SelectUnitFormControl
} from './edit-modal.styles';
import { useDispatch } from 'react-redux';
import apiClient from '../../../api/api-client';
import { UploadImageModal } from './upload-image-modal';
import { buildProductDefaultImageUrl, buildProductMainImageUrl } from '../../../utils/productImage';
import { StringPanelAccordion } from '../accordion-panels/string-panel-accordion';
import { SelectChangeEvent } from '@mui/material';
import { HealthClaimsPanel } from '../accordion-panels/health-claims-panel';
import { ModifiersPanel } from '../accordion-panels/modifiers-panel';
import { QuitEditPopup } from './quit-edit-popup';
import { PricelistPathSelection } from '../pricelist-path-selection/pricelist-path-selection';

interface EditModalProps {
  onClose: (isUpdateSuccess?: boolean) => void;
  isOpen: boolean;
  isAdd: boolean;
}

const TextDataItem: FC<{
  id: string;
  label: string;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  rows?: number;
  isLargeField?: boolean;
}> = ({ id, label, name, value, onChange, disabled, required, rows, isLargeField }) => {
  return (
    <TextDataValue
      id={id}
      label={label}
      name={name}
      value={value ?? ''}
      size='small'
      multiline
      onChange={onChange}
      rows={rows ? rows : !value || value.length < 25 ? 1 : 2}
      inputProps={{
        style: InputStyle
      }}
      InputLabelProps={{
        style: LabelStyle
      }}
      disabled={!!disabled}
      required={!!required}
      style={isLargeField ? { marginBottom: '40px' } : {}}
      isDisabled={!!disabled}
    />
  );
};

const NumberDataItem: FC<{
  id: string;
  label: string;
  name: string;
  value: number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  error: boolean;
}> = ({ id, label, name, value, onChange, disabled, required, error }) => {
  return (
    <TextDataValue
      id={id}
      label={label}
      name={name}
      type='number'
      value={value ?? ''}
      size='small'
      onChange={onChange}
      inputProps={{
        style: InputStyle
      }}
      InputLabelProps={{
        style: LabelStyle
      }}
      disabled={!!disabled}
      required={!!required}
      error={error}
      isDisabled={!!disabled}
    />
  );
};

const RadioButtonValue: FC<{
  id: string;
  label: string;
  name: string;
  value: boolean | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
}> = ({ id, label, name, value, onChange, required, disabled }) => {
  return (
    <RadioFormLabel id={id}>
      {label}
      <RadiobButtonsGroup
        row
        value={value === null ? null : value ? 'yes' : 'no'}
        onChange={onChange}
      >
        <RadioButtonLabel
          name={name}
          value={'no'}
          control={
            <RadioButton
              id={`${id}-radio-button-no`}
              size='small'
              required={required}
              disabled={disabled}
            />
          }
          label='No'
          disabled={disabled}
        />
        <RadioButtonLabel
          name={name}
          value={'yes'}
          control={
            <RadioButton
              id={`${id}-radio-button-yes`}
              size='small'
              required={required}
              disabled={disabled}
            />
          }
          label='Yes'
          disabled={disabled}
        />
      </RadiobButtonsGroup>
    </RadioFormLabel>
  );
};

export const EditModal: FC<EditModalProps> = ({ onClose, isOpen, isAdd }) => {
  const [isChange, setIsChange] = useState(false);
  const [invalidValuesArray, setInvalidValuesArray] = useState<string[]>([]);
  const [upcError, setUpcError] = useState(false);
  const [generalError, setGeneralError] = useState(false);
  const [isShowImageNotification, setShowImageNotification] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | false>();

  const handleChange =
    (panel: string, bodyLength: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded && currentPricelist !== '' ? panel : false);
    };

  const [isQuitEditPopupOpen, setIsQuitEditPopupOpen] = useState<boolean>(false);
  const [isQuitAddPopupOpen, setIsQuitAddPopupOpen] = useState<boolean>(false);

  const product: Product = useSelector((state: RootState) =>
    singleProductSelectors.selectProduct(state.singleProduct)
  );

  const pricelistCategories: Record<string, string[]> = useSelector((state: RootState) =>
    singleProductSelectors.selectPricelistCategories(state.singleProduct)
  );

  const tenantsList = useSelector((state: RootState) =>
    productsSelectors.selectTenantsAndLocations(state.products)
  );

  const isPricelistCategoriesLoading: boolean = useSelector((state: RootState) =>
    singleProductSelectors.selectIsPricelistCategoriesLoading(state.singleProduct)
  );

  const dispatch = useDispatch();

  const currentTenant = useSelector((state: RootState) =>
    singleProductSelectors.selectCurrentTenant(state.singleProduct)
  );

  const currentPricelist = useSelector((state: RootState) =>
    singleProductSelectors.selectCurrentPricelist(state.singleProduct)
  );

  const unitList: string[] = ['count', 'lb', 'bunch'];

  const panels = [
    <ModifiersPanel
      key='modifiers-panel'
      allowEditing={currentPricelist !== ''}
      expanded={expanded}
      setIsChange={setIsChange}
      handleAccordionChange={handleChange}
    />,
    <HealthClaimsPanel
      key='health-claims-panel'
      allowEditing={currentPricelist !== ''}
      expanded={expanded}
      setIsChange={setIsChange}
      handleAccordionChange={handleChange}
    />
  ];

  useEffect(() => {
    if (product.pricelistId) {
      dispatch(singleProductActions.getPricelistModifiers(product.pricelistId));
      dispatch(singleProductActions.getPricelistCategories(product.pricelistId));
    }
  }, [dispatch, product.pricelistId]);

  function getProductName(upc: string) {
    if (upc.length >= 10) {
      return upc;
    }
    if (tenantsList[currentTenant].tenantName) {
      let productName = `${tenantsList[currentTenant].tenantName}-cst-upc-${upc}`;
      productName = productName.replace(/ /g, '-');
      return productName;
    }
    return '';
  }

  function handleClose() {
    if (isChange) setIsQuitEditPopupOpen(true);
    else onClose();
  }

  function onQuitEditing() {
    setIsQuitEditPopupOpen(false);
    onClose();
  }

  function onKeepEditing() {
    setIsQuitEditPopupOpen(false);
  }

  function onQuitAdding() {
    setIsQuitAddPopupOpen(false);
    dispatch(singleProductActions.resetAllFields());
    setIsChange(false);
  }

  function onKeepAdding() {
    setIsQuitAddPopupOpen(false);
  }

  function handleTextChange(event: ChangeEvent<HTMLInputElement>) {
    setIsChange(true);
    dispatch(
      singleProductActions.updateProduct({
        fieldName: event.target.name,
        value: event.target.value
      })
    );
    if (event.target.name === 'upc' && isAdd) {
      const productName = getProductName(event.target.value);
      dispatch(
        singleProductActions.updateProduct({
          fieldName: 'productName',
          value: productName
        })
      );
    }
  }

  function handleNumberChange(event: ChangeEvent<HTMLInputElement>) {
    setIsChange(true);
    let numericValue = parseFloat(event.target.value);
    dispatch(
      singleProductActions.updateProduct({ fieldName: event.target.name, value: numericValue })
    );
  }

  function handleBooleanChange(
    event: ChangeEvent<HTMLInputElement>,
    value?: string,
    reverseValue?: boolean
  ) {
    setIsChange(true);
    let val = event.target.value === 'yes';
    if (reverseValue) {
      val = !val;
    }
    dispatch(
      singleProductActions.updateProduct({
        fieldName: event.target.name,
        value: val
      })
    );
  }

  function handleSelectChange(event: SelectChangeEvent<any>) {
    if (
      (event.target.name === 'tenantId' ||
        event.target.name === 'locationId' ||
        event.target.name === 'pricelistId') &&
      isChange
    ) {
      setIsQuitAddPopupOpen(true);
    } else {
      dispatch(
        singleProductActions.updateProduct({
          fieldName: event.target.name,
          value: event.target.value
        })
      );
      if (event.target.name === 'tenantId') {
        dispatch(singleProductActions.resetFields(['locationId', 'productName']));
      }
      if (event.target.name === 'tenantId' || event.target.name === 'locationId') {
        dispatch(singleProductActions.resetFields(['pricelistId']));
      }
      if (event.target.name === 'subType' || event.target.name === 'unit') {
        setIsChange(true);
      }
      if (event.target.name === 'type') {
        setIsChange(true);
        dispatch(singleProductActions.resetFields(['subType']));
      }
    }
  }

  function handleImageChange(newImageUrl?: string) {
    setIsChange(true);
    dispatch(
      singleProductActions.updateProduct({ fieldName: 'mainImage', value: newImageUrl ?? '' })
    );
  }

  function handleImageLoad(initialLoad: boolean) {
    if (isAdd) {
      return;
    }
    if (!initialLoad) {
      setShowImageNotification(true);
    }
  }

  function validateNumericValue(event: ChangeEvent<HTMLInputElement>) {
    const isValid = !(parseFloat(event.target.value) < 0);
    if (!isValid && !invalidValuesArray.includes(event.target.name)) {
      setInvalidValuesArray([...invalidValuesArray, event.target.name]);
    }
    if (isValid && invalidValuesArray.includes(event.target.name)) {
      const newInvalidValueArray = invalidValuesArray.filter(
        (value) => value !== event.target.name
      );
      setInvalidValuesArray(newInvalidValueArray);
    }
    return isValid;
  }

  function isUpdateButtonEnable() {
    const canUpdate =
      isChange &&
      product.price &&
      product.displayName &&
      product.type &&
      product.subType &&
      product.isInStock !== null &&
      product.isInactive !== null &&
      invalidValuesArray.length === 0 &&
      currentPricelist !== '' &&
      product.unit &&
      (!isAdd || product.upc);
    return canUpdate;
  }

  async function handleUpdateButton() {
    try {
      await apiClient.updateProduct(product, isAdd);
      dispatch(productsActions.getProducts());
      onClose(true);
    } catch (error: any) {
      if (error.response.data.statusCode === 409) {
        setUpcError(true);
      } else {
        setGeneralError(true);
      }
    }
  }

  function errorHandler() {
    setGeneralError(false);
    setUpcError(false);
  }

  function closeImageMessageNotification() {
    setShowImageNotification(false);
  }

  const isLoading: boolean = useSelector((state: RootState) =>
    singleProductSelectors.selectIsModalLoading(state.singleProduct)
  );

  if (isLoading) {
    return (
      <StyledModal
        onClose={() => {
          handleClose();
        }}
        open={isOpen}
        id='edit-modal'
      >
        <ModalContent id='edit-modal-content'>
          <Spinner />
        </ModalContent>
      </StyledModal>
    );
  } else {
    return (
      <StyledModal
        onClose={() => {
          handleClose();
        }}
        open={isOpen}
        id='edit-modal'
      >
        <ModalContent id='edit-modal-content'>
          <QuitEditPopup
            isOpen={isQuitEditPopupOpen}
            onQuitEditing={onQuitEditing}
            onKeepEditing={onKeepEditing}
          />
          <QuitEditPopup
            isOpen={isQuitAddPopupOpen}
            onQuitEditing={onQuitAdding}
            onKeepEditing={onKeepAdding}
          />
          <ErrorSnackbar
            id='edit-error-notification'
            open={generalError || upcError}
            autoHideDuration={3000}
            onClose={errorHandler}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity='error' onClose={errorHandler}>
              {upcError
                ? `UPC ${product.upc} already exists in this pricelist`
                : `update error. please try again.`}
            </Alert>
          </ErrorSnackbar>
          <CloseModalButton
            onClick={() => {
              handleClose();
            }}
            id='edit-modal-top-close-button'
          >
            <CloseModalIcon />
          </CloseModalButton>
          {!isAdd && (
            <div>
              <Title id='edit-modal-main-title'>Product details</Title>
              <Subtitle id='edit-modal-subtitle'>
                {product.tenantName} - {product.locationName} - {product.pricelistName}
              </Subtitle>
            </div>
          )}
          {isAdd && <Title id='add-modal-main-title'>Create new product</Title>}
          <form
            onSubmit={(event: any) => {
              event.preventDefault();
            }}
          >
            <ModalBody>
              {isAdd && (
                <PricelistPathSelection
                  handleSelectChange={handleSelectChange}
                  showPricelist={true}
                />
              )}
              <BodySection id='edit-body-section-1'>
                <SectionColumn width={'180px'}>
                  <ImageDataValue
                    label='Product image'
                    multiline
                    rows={6.45}
                    InputLabelProps={{ shrink: true }}
                    disabled={true}
                    isDisabled={true}
                  />
                  {currentPricelist !== '' && (
                    <UploadImageModal
                      mainImage={buildProductMainImageUrl(product)}
                      defaultImage={buildProductDefaultImageUrl(product)}
                      onImageLoad={handleImageLoad}
                      onImageChange={handleImageChange}
                    />
                  )}
                </SectionColumn>
                <SectionColumn width={'200px'}>
                  <TextDataItem
                    id='edit-display-name'
                    label='Product display name'
                    name='displayName'
                    value={product.displayName}
                    onChange={handleTextChange}
                    required
                    disabled={currentPricelist === ''}
                  />
                  <TextDataItem
                    id='edit-upc'
                    label={isAdd ? 'UPC / Identify value *' : 'UPC / Identify value'}
                    name='upc'
                    value={product.upc}
                    onChange={handleTextChange}
                    disabled={currentPricelist === ''}
                  />
                  <TextDataItem
                    id='edit-brand'
                    label='Product brand'
                    name='brand'
                    value={product.brand}
                    onChange={handleTextChange}
                    disabled={currentPricelist === ''}
                  />
                </SectionColumn>
                <SectionColumn width={'200px'}>
                  <SelectCategoryFormControl size='small'>
                    <SelectInputLabel
                      id='edit-main-category-label'
                      isDisabled={currentPricelist === ''}
                    >
                      Main category *
                    </SelectInputLabel>
                    <StyledSelectField
                      id='edit-main-category'
                      label='Main category *'
                      labelId='edit-main-category-label'
                      name='type'
                      value={product.type}
                      onChange={handleSelectChange}
                      disabled={isPricelistCategoriesLoading || currentPricelist === ''}
                      required
                      MenuProps={SelectMenuProps}
                      isDisabled={currentPricelist === ''}
                    >
                      {Object.keys(pricelistCategories).map((category: string) => (
                        <StyledSelectMenuItem
                          id={`category-${category}`}
                          key={`category-${category}`}
                          value={category}
                        >
                          {capitalize(category)}
                        </StyledSelectMenuItem>
                      ))}
                    </StyledSelectField>
                  </SelectCategoryFormControl>
                  <SelectSubcategoryFormControl size='small'>
                    <SelectInputLabel
                      id='edit-sub-category-label'
                      isDisabled={currentPricelist === ''}
                    >
                      {' '}
                      Sub category *
                    </SelectInputLabel>
                    <StyledSelectField
                      id='edit-sub-category'
                      label='Sub category *'
                      labelId='edit-sub-category-label'
                      name='subType'
                      value={product.subType}
                      onChange={handleSelectChange}
                      disabled={isPricelistCategoriesLoading || currentPricelist === ''}
                      required
                      MenuProps={SelectMenuProps}
                      isDisabled={currentPricelist === ''}
                    >
                      {pricelistCategories[product.type]?.map((subCategory: string) => (
                        <StyledSelectMenuItem
                          id={`subcategory-${subCategory}`}
                          key={`subcategory-${subCategory}`}
                          value={subCategory}
                        >
                          {capitalize(subCategory)}
                        </StyledSelectMenuItem>
                      ))}
                    </StyledSelectField>
                  </SelectSubcategoryFormControl>
                  <TextDataItem
                    id='edit-product-catalog-name'
                    label='Product catalog name'
                    name='productName'
                    value={product.productName}
                    onChange={handleTextChange}
                    disabled
                  />
                </SectionColumn>
                <SectionColumn width={'180px'}>
                  <NumberDataItem
                    id='edit-unit-quantity'
                    label='Unit quantity'
                    name='unitQuantity'
                    value={product.unitQuantity}
                    onChange={(event) => {
                      if (validateNumericValue(event)) handleNumberChange(event);
                    }}
                    error={invalidValuesArray.includes('unitQuantity')}
                    disabled={currentPricelist === ''}
                  />
                  <NumberDataItem
                    id='edit-min-quantity'
                    label='Min order quantity'
                    name='minQuantity'
                    value={product.minQuantity}
                    onChange={(event) => {
                      if (validateNumericValue(event)) handleNumberChange(event);
                    }}
                    error={invalidValuesArray.includes('minQuantity')}
                    disabled={currentPricelist === ''}
                  />
                  <NumberDataItem
                    id='edit-max-quantity'
                    label='Max order quantity'
                    name='maxQuantity'
                    value={product.maxQuantity}
                    onChange={(event) => {
                      if (validateNumericValue(event)) handleNumberChange(event);
                    }}
                    error={invalidValuesArray.includes('maxQuantity')}
                    disabled={currentPricelist === ''}
                  />
                </SectionColumn>
                <SectionColumn width={'180px'}>
                  {!isAdd && (
                    <TextDataItem
                      id='edit-unit-size'
                      label='Unit size'
                      name='unit'
                      value={product.unit}
                      onChange={handleTextChange}
                      required
                      disabled={currentPricelist === ''}
                    />
                  )}
                  {isAdd && (
                    <SelectUnitFormControl size='small'>
                      <SelectInputLabel
                        id='edit-unit-input-label'
                        isDisabled={currentPricelist === ''}
                      >
                        {' '}
                        Unit size *
                      </SelectInputLabel>
                      <StyledSelectField
                        id='edit-unit-size'
                        label='Unit size'
                        labelId='edit-unit-size-label'
                        name='unit'
                        value={product.unit}
                        onChange={handleSelectChange}
                        disabled={currentPricelist === ''}
                        MenuProps={SelectMenuProps}
                        required
                        isDisabled={currentPricelist === ''}
                      >
                        {unitList.map((unit: string) => (
                          <StyledSelectMenuItem
                            id={`unit-${unit}`}
                            key={`unit-${unit}`}
                            value={unit}
                          >
                            {capitalize(unit)}
                          </StyledSelectMenuItem>
                        ))}
                      </StyledSelectField>
                    </SelectUnitFormControl>
                  )}
                  <NumberDataItem
                    id='edit-unit-increment'
                    label='Unit increment'
                    name='unitIncrement'
                    value={product.unitIncrement}
                    onChange={(event) => {
                      if (validateNumericValue(event)) handleNumberChange(event);
                    }}
                    error={invalidValuesArray.includes('unitIncrement')}
                    disabled={currentPricelist === ''}
                  />
                  <NumberDataItem
                    id='edit-price'
                    label='Price'
                    name='price'
                    value={product.price}
                    onChange={(event) => {
                      if (validateNumericValue(event)) handleNumberChange(event);
                    }}
                    required
                    error={invalidValuesArray.includes('price')}
                    disabled={currentPricelist === ''}
                  />
                </SectionColumn>
              </BodySection>
              <SectionDivider />
              <BodySection id='edit-body-section-2'>
                <SectionColumn width={'180px'}>
                  <RadioButtonValue
                    id='edit-organic'
                    label='Organic'
                    name='organic'
                    value={product.organic}
                    onChange={handleBooleanChange}
                    disabled={currentPricelist === ''}
                  />
                  <RadioButtonValue
                    id='edit-in-stock'
                    label='In stock *'
                    name='isInStock'
                    value={product.isInStock}
                    onChange={handleBooleanChange}
                    required
                    disabled={currentPricelist === ''}
                  />
                  <RadioButtonValue
                    id='edit-taxable'
                    label='Taxable'
                    name='isTaxable'
                    value={product.isTaxable}
                    onChange={handleBooleanChange}
                    disabled={currentPricelist === ''}
                  />
                </SectionColumn>
                <SectionColumn width={'200px'}>
                  <RadioButtonValue
                    id='edit-is-alcoholic'
                    label='Alcoholic'
                    name='isAlcohol'
                    value={product.isAlcohol}
                    onChange={handleBooleanChange}
                    disabled={currentPricelist === ''}
                  />
                  <RadioButtonValue
                    id='edit-active'
                    label='Active *'
                    name='isInactive'
                    value={product.isInactive === null ? null : !product.isInactive}
                    onChange={(event) => handleBooleanChange(event, '', true)}
                    required
                    disabled={currentPricelist === ''}
                  />
                  <RadioButtonValue
                    id='edit-pickup'
                    label='Pickup only'
                    name='isPickupOnly'
                    value={product.isPickupOnly}
                    onChange={handleBooleanChange}
                    disabled={currentPricelist === ''}
                  />
                </SectionColumn>
                <SectionColumn width={'200px'}>
                  <RadioButtonValue
                    id='edit-sell-by-quantity'
                    label='Sell by quantity?'
                    name='sellByQty'
                    value={product.sellByQty}
                    onChange={handleBooleanChange}
                    disabled={currentPricelist === ''}
                  />
                  <RadioButtonValue
                    id='edit-on-homepage'
                    label='On homepage?'
                    name='isOnHomepage'
                    value={product.isOnHomepage}
                    onChange={handleBooleanChange}
                    disabled={currentPricelist === ''}
                  />
                </SectionColumn>
                <SectionColumn width={'375px'}>
                  <TextDataItem
                    id='edit-ingredients'
                    label='Ingredients'
                    name='ingredients'
                    value={product.ingredients}
                    onChange={handleTextChange}
                    rows={2}
                    disabled={currentPricelist === ''}
                  />
                  <TextDataItem
                    id='edit-description'
                    label='Description'
                    name='description'
                    value={product.description}
                    onChange={handleTextChange}
                    rows={2}
                    disabled={currentPricelist === ''}
                  />
                  <TextDataItem
                    id='edit-disclaimer'
                    label='Disclaimer'
                    name='disclaimer'
                    value={product.disclaimer}
                    onChange={handleTextChange}
                    rows={2}
                    disabled={currentPricelist === ''}
                  />
                </SectionColumn>
              </BodySection>
              <SectionDividerMarginTop />
              <StringPanelAccordion panels={panels} />
            </ModalBody>
            <ModalFooter id='edit-modal-footer'>
              <FooterDataValue>Mandatory fields *</FooterDataValue>
              <CloseButton id='edit-modal-bottom-close-button' onClick={handleClose}>
                Close
              </CloseButton>
              <UpdateButton
                id='edit-modal-update-button'
                onClick={handleUpdateButton}
                disabled={!isUpdateButtonEnable()}
              >
                {isAdd ? 'Create product' : 'Update product'}
              </UpdateButton>

              <Snackbar
                id='edit-image-success-notification'
                open={isShowImageNotification}
                autoHideDuration={2000}
                onClose={closeImageMessageNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <Alert severity='success'> Image has updated </Alert>
              </Snackbar>
            </ModalFooter>
          </form>
        </ModalContent>
      </StyledModal>
    );
  }
};
